/**
 * TimezoneSelect Component
 *
 * A dropdown select component for choosing a timezone.
 * Displays timezones grouped by region for easier selection.
 *
 * @example
 * <TimezoneSelect
 *   value={timezone}
 *   onChange={setTimezone}
 * />
 */

import { COMMON_TIMEZONES, formatTimezoneForDisplay } from '../../lib/datetime';

interface TimezoneSelectProps {
  /**
   * Currently selected timezone (IANA format).
   */
  value: string;
  
  /**
   * Callback when timezone selection changes.
   */
  onChange: (value: string) => void;
  
  /**
   * Whether the select is disabled.
   */
  disabled?: boolean;
  
  /**
   * Additional CSS classes for the select element.
   */
  className?: string;
  
  /**
   * ID for the select element (for label association).
   */
  id?: string;
}

/**
 * TimezoneSelect - A grouped dropdown for selecting timezones.
 *
 * Uses native HTML select with optgroup for grouping timezones by region.
 * For a more styled version, integrate with your UI library's Select component.
 */
export function TimezoneSelect({
  value,
  onChange,
  disabled = false,
  className = '',
  id,
}: TimezoneSelectProps): JSX.Element {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`
        w-full px-3 py-2 
        bg-white dark:bg-gray-800 
        border border-gray-300 dark:border-gray-600 
        rounded-md shadow-sm
        text-gray-900 dark:text-gray-100
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
        disabled:bg-gray-100 dark:disabled:bg-gray-700 
        disabled:cursor-not-allowed
        ${className}
      `.trim()}
    >
      {(Object.entries(COMMON_TIMEZONES) as [string, readonly string[]][]).map(([region, zones]) => (
        <optgroup key={region} label={region}>
          {zones.map((tz: string) => (
            <option key={tz} value={tz}>
              {formatTimezoneForDisplay(tz)}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default TimezoneSelect;
